import type { ProductionItem } from '@qlk/shared';
import { PRODUCTION_STAGE_LABEL, PRODUCTION_TOOL_LABEL } from '@qlk/shared';
import { Check, FileVideo, Image as ImageIcon, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { EmptyState, ngayGioNgan, Nut, StatusBadge, useKhoaCuonTrang } from '../../../ui/index.js';
import { TrackPicker } from '../../music/components/TrackPicker.js';
import { taiFileLen, useCapNhatItem, useDatChecklist, useItem, useSinhKichBan } from '../api.js';

export interface DrawerChiTietProps {
  itemId: string;
  onDong: () => void;
  coSua: boolean;
}

function gioVn(iso: string): string {
  return ngayGioNgan(iso);
}

function mb(byte: number): string {
  return `${(byte / 1024 / 1024).toFixed(1)} MB`;
}

interface MocLichSu {
  khi: string;
  viec: string;
  boi: string | null;
}

/** Lịch sử dựng từ dữ liệu sẵn có của thẻ (xem docs/modules/production.md). */
function dungLichSu(item: ProductionItem): MocLichSu[] {
  const moc: MocLichSu[] = [{ khi: item.createdAt, viec: 'Tạo thẻ sản xuất', boi: null }];

  for (const c of item.checklist) {
    if (c.checked && c.checkedAt) {
      moc.push({ khi: c.checkedAt, viec: `Tích "${c.label}"`, boi: c.checkedBy });
    }
  }

  moc.push({
    khi: item.updatedAt,
    viec: `Cập nhật gần nhất · đang ở bước ${PRODUCTION_STAGE_LABEL[item.stage]}`,
    boi: null,
  });

  return moc.sort((a, b) => b.khi.localeCompare(a.khi));
}

function Khoi({ tieuDe, children }: { tieuDe: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-vien px-6 py-5">
      <h3 className="mb-3 text-tieu-de-khoi font-semibold text-chu">{tieuDe}</h3>
      {children}
    </section>
  );
}

export function DrawerChiTiet({ itemId, onDong, coSua }: DrawerChiTietProps) {
  // Drawer cũng là lớp phủ toàn màn hình: khoá cuộn trang nền như mọi hộp thoại.
  useKhoaCuonTrang();

  const truyVan = useItem(itemId);
  const capNhat = useCapNhatItem(itemId);
  const datChecklist = useDatChecklist(itemId);
  const sinhKichBan = useSinhKichBan(itemId);

  const [kichBan, setKichBan] = useState<string | null>(null);
  const [tienTrinh, setTienTrinh] = useState<{ kind: string; phanTram: number } | null>(null);
  const [loiTai, setLoiTai] = useState<string | null>(null);
  const huyTai = useRef<(() => void) | null>(null);

  const item = truyVan.data;

  useEffect(() => {
    setKichBan(null);
    setLoiTai(null);
  }, [itemId]);

  useEffect(() => {
    const thoat = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDong();
    };
    window.addEventListener('keydown', thoat);
    return () => {
      window.removeEventListener('keydown', thoat);
    };
  }, [onDong]);

  async function chonFile(kind: 'video' | 'thumbnail', file: File | undefined) {
    if (!file) return;
    setLoiTai(null);
    setTienTrinh({ kind, phanTram: 0 });

    const { promise, huy } = taiFileLen(itemId, kind, file, (t) => {
      setTienTrinh({ kind, phanTram: Math.round((t.daGui / t.tong) * 100) });
    });
    huyTai.current = huy;

    try {
      await promise;
      await truyVan.refetch();
    } catch (err) {
      setLoiTai(err instanceof Error ? err.message : 'Tải file thất bại');
    } finally {
      setTienTrinh(null);
      huyTai.current = null;
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Đóng bảng chi tiết"
        className="flex-1 bg-black/30"
        onClick={onDong}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Chi tiết video"
        className="flex w-full max-w-[560px] flex-col overflow-y-auto bg-be-mat shadow-xl"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-vien bg-be-mat px-6 py-4">
          <div className="min-w-0">
            <p className="text-phu text-chu-mo">
              {item ? `${item.channelName} · ${PRODUCTION_STAGE_LABEL[item.stage]}` : 'Đang tải…'}
            </p>
            <h2 className="truncate text-tieu-de-hop font-bold text-chu">{item?.title ?? ''}</h2>
          </div>
          <button
            type="button"
            onClick={onDong}
            aria-label="Đóng"
            className="vung-bam inline-flex min-w-[44px] items-center justify-center rounded-[10px] text-chu-phu hover:bg-neutral-nen"
          >
            <X size={20} />
          </button>
        </header>

        {truyVan.isPending && <p className="px-6 py-10 text-co-ban text-chu-phu">Đang tải…</p>}

        {truyVan.isError && (
          <EmptyState loi title="Không tải được thẻ" description={truyVan.error.message} />
        )}

        {item && (
          <>
            <Khoi tieuDe="Metadata">
              <dl className="space-y-2 text-co-ban">
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 text-chu-mo">Tiêu đề</dt>
                  <dd className="text-chu">{item.metadata?.title ?? item.title}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 text-chu-mo">Mô tả</dt>
                  <dd className="text-chu-phu">{item.metadata?.description ?? '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 text-chu-mo">Tag</dt>
                  <dd className="text-chu-phu">{item.metadata?.tags?.join(', ') ?? '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 text-chu-mo">Công cụ</dt>
                  <dd className="text-chu-phu">
                    {item.tool ? (PRODUCTION_TOOL_LABEL[item.tool] ?? item.tool) : '—'}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 text-chu-mo">Khai báo AI</dt>
                  <dd>
                    <StatusBadge tone={item.aiDisclosure ? 'info' : 'neutral'}>
                      {item.aiDisclosure ? 'Có khai báo' : 'Không'}
                    </StatusBadge>
                  </dd>
                </div>
              </dl>
            </Khoi>

            <Khoi tieuDe="Kịch bản">
              <textarea
                value={kichBan ?? item.scriptText ?? ''}
                onChange={(e) => {
                  setKichBan(e.target.value);
                }}
                readOnly={!coSua}
                rows={8}
                className="w-full rounded-[10px] border border-vien bg-be-mat p-3 text-co-ban text-chu"
                placeholder="Chưa có kịch bản."
              />
              {coSua && (
                <div className="mt-3 flex flex-wrap gap-3">
                  <Nut
                    kieu="phu"
                    dangChay={sinhKichBan.isPending}
                    onClick={() => {
                      sinhKichBan.mutate(
                        {},
                        {
                          onSuccess: (du) => {
                            setKichBan(du.scriptText);
                          },
                        },
                      );
                    }}
                  >
                    <Sparkles size={16} aria-hidden="true" />
                    Viết kịch bản bằng AI
                  </Nut>
                  <Nut
                    kieu="chinh"
                    disabled={kichBan === null}
                    dangChay={capNhat.isPending}
                    onClick={() => {
                      capNhat.mutate({ scriptText: kichBan ?? '' });
                    }}
                  >
                    Lưu kịch bản
                  </Nut>
                </div>
              )}
            </Khoi>

            <Khoi tieuDe="File">
              <ul className="space-y-3 text-co-ban">
                <li className="flex items-center gap-3">
                  <FileVideo size={18} aria-hidden="true" className="text-chu-mo" />
                  <span className="min-w-0 flex-1 truncate text-chu-phu">
                    {item.videoFileUrl ?? 'Chưa có file video'}
                  </span>
                  {coSua && (
                    <label className="vung-bam inline-flex cursor-pointer items-center rounded-[10px] border border-vien px-3 text-co-ban font-semibold text-chu">
                      Chọn video
                      <input
                        type="file"
                        className="sr-only"
                        accept=".mp4,.mov,.mkv,.webm"
                        onChange={(e) => {
                          void chonFile('video', e.target.files?.[0]);
                        }}
                      />
                    </label>
                  )}
                </li>
                <li className="flex items-center gap-3">
                  <ImageIcon size={18} aria-hidden="true" className="text-chu-mo" />
                  <span className="min-w-0 flex-1 truncate text-chu-phu">
                    {item.thumbnailUrl ?? 'Chưa có thumbnail'}
                  </span>
                  {coSua && (
                    <label className="vung-bam inline-flex cursor-pointer items-center rounded-[10px] border border-vien px-3 text-co-ban font-semibold text-chu">
                      Chọn ảnh
                      <input
                        type="file"
                        className="sr-only"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={(e) => {
                          void chonFile('thumbnail', e.target.files?.[0]);
                        }}
                      />
                    </label>
                  )}
                </li>
              </ul>

              {tienTrinh && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-phu text-chu-phu">
                    <span>
                      Đang tải {tienTrinh.kind === 'video' ? 'video' : 'thumbnail'} —{' '}
                      {tienTrinh.phanTram}%
                    </span>
                    <Nut
                      kieu="nhe"
                      className="h-8 min-h-0 px-2 text-phu"
                      onClick={() => huyTai.current?.()}
                    >
                      Huỷ
                    </Nut>
                  </div>
                  <div
                    role="progressbar"
                    aria-valuenow={tienTrinh.phanTram}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className="mt-1 h-2 overflow-hidden rounded-full bg-neutral-nen"
                  >
                    <div
                      className="h-full bg-nhan transition-[width]"
                      style={{ width: `${String(tienTrinh.phanTram)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-phu text-chu-mo">
                    Giới hạn: video {mb(20 * 1024 * 1024 * 1024)}, thumbnail {mb(5 * 1024 * 1024)}.
                  </p>
                </div>
              )}

              {loiTai && <p className="mt-3 text-phu text-bad-chu">{loiTai}</p>}
            </Khoi>

            <Khoi tieuDe="Nhạc">
              <TrackPicker
                channelId={item.channelId}
                value={item.trackIds}
                onChange={(ids) => {
                  if (!coSua) return;
                  capNhat.mutate({ trackIds: ids });
                }}
              />
            </Khoi>

            <Khoi tieuDe="Checklist trước khi đăng">
              {item.checklist.length === 0 ? (
                <p className="text-phu text-chu-mo">Chưa có mục checklist nào.</p>
              ) : (
                <ul className="space-y-2">
                  {item.checklist.map((c) => (
                    <li key={c.key}>
                      <label className="flex items-start gap-3 text-co-ban">
                        <input
                          type="checkbox"
                          className="mt-1 size-4"
                          checked={c.checked}
                          disabled={!coSua || datChecklist.isPending}
                          onChange={(e) => {
                            datChecklist.mutate({ key: c.key, checked: e.target.checked });
                          }}
                        />
                        <span>
                          <span className="text-chu">{c.label}</span>
                          {c.checked && c.checkedAt && (
                            <span className="mt-0.5 block text-phu text-chu-mo">
                              <Check size={12} aria-hidden="true" className="inline" />{' '}
                              {c.checkedBy ?? 'Hệ thống'} · {gioVn(c.checkedAt)}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </Khoi>

            <Khoi tieuDe="Lịch sử">
              <ol className="space-y-2 text-co-ban">
                {dungLichSu(item).map((m, i) => (
                  <li key={`${m.khi}-${String(i)}`} className="flex gap-3">
                    <span className="so-lieu w-36 shrink-0 text-phu text-chu-mo">
                      {gioVn(m.khi)}
                    </span>
                    <span className="text-chu-phu">
                      {m.viec}
                      {m.boi ? ` · ${m.boi}` : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </Khoi>
          </>
        )}
      </aside>
    </div>
  );
}
